import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search,
  GitBranch,
  Zap,
  BarChart3,
  Layers,
  Sparkles,
  Brain,
  Lightbulb,
  Rocket,
  Building2,
  ChevronRight,
  HelpCircle,
  Settings,
  Target,
  Route,
  Compass,
  Shield,
  Briefcase,
  Play,
  Layers as LayersIcon,
  Lock
} from 'lucide-react';

const Sidebar = () => {
  const location = useLocation();

  const menuItems = [
    
    { 
      icon: Route, 
      label: 'Product AI Roadmap', 
      path: '/roadmap-innovation',
      description: 'AI-powered planning',
      badge: 'New'
    },
    { 
      icon: LayersIcon, 
      label: 'MBSE OPM Flow', 
      path: '/mbse-opm-flow',
      description: 'Object-Process Methodology',
      badge: 'New'
    },
    { 
      icon: Briefcase, 
      label: 'Business Models', 
      path: '/business-model',
      description: 'Innovation frameworks',
      badge: "Beta"
    },
    { 
      icon: Building2, 
      label: 'Architecture', 
      path: '/algorithm-optimization',
      description: 'System design',
      badge: 'Beta'
    },
    { 
      icon: Sparkles, 
      label: 'AI Ideation', 
      path: '/ideation',
      description: 'Generate breakthrough ideas',
      badge: null,
      locked: true
    },
    { 
      icon: Target, 
      label: 'TRIZ Solver', 
      path: '/problem-solving',
      description: 'Systematic innovation',
      badge: null,
      locked: true
    },
    { 
      icon: Compass, 
      label: 'Market Discovery', 
      path: '/market-discovery',
      description: 'Find opportunities',
      badge: null,
      locked: true
    },
    { 
      icon: Shield, 
      label: 'IP Strategy', 
      path: '/ip-strategy',
      description: 'Patent development',
      badge: null,
      locked: true
    }  
  ];

  return (
    <motion.aside
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="w-72 h-full flex flex-col" style={{ backgroundColor: 'rgb(var(--color-bg-white))', borderRight: '1px solid rgb(var(--color-border))' }}
    >
      {/* Logo Section */}
      <div className="px-6 py-8 border-b" style={{ borderColor: 'rgb(var(--color-border))' }}>
        <Link to="/home" className="flex items-center space-x-3">
          <div className="w-10 h-10 sqsft-gradient-primary rounded-xl flex items-center justify-center shadow-lg">
            <Rocket className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl sqsft-heading-2">SquareShift</h1>
            <p className="text-xs sqsft-caption">TRIZ Innovation</p>
          </div>
        </Link>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 px-4 py-8 overflow-y-auto scrollbar-thin">
        <div className="space-y-2">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <motion.div
                key={item.path}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={item.path}
                  className={`
                    group relative flex items-center px-4 py-4 rounded-xl transition-all duration-200
                    ${isActive 
                      ? 'sqsft-nav-item active' 
                      : 'sqsft-nav-item'
                    }
                  `}
                >
                  {isActive && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute left-0 w-1 h-full sqsft-gradient-primary rounded-r-full"
                      initial={false}
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  
                  <div className={`
                    w-10 h-10 rounded-xl flex items-center justify-center mr-4 transition-all duration-200
                    ${isActive 
                      ? 'sqsft-gradient-primary text-white shadow-lg' 
                      : 'group-hover:bg-gray-200'
                    }
                  `} style={!isActive ? { backgroundColor: 'rgb(var(--color-bg-light))', color: 'rgb(var(--color-text-secondary))' } : {}}>
                    <Icon className="w-5 h-5" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm sqsft-body font-medium truncate ${isActive ? 'font-semibold' : ''} flex items-center`}>
                        {item.label}
                        {item.locked && <Lock className="w-3 h-3 ml-2 text-gray-400" />}
                      </span>
                      {item.badge && (
                        <span className={`
                          ml-2 px-2 py-0.5 text-xs sqsft-badge rounded-full
                          ${item.badge === 'AI' 
                            ? 'sqsft-badge-primary'
                            : item.badge === 'New'
                            ? 'sqsft-badge-success'
                            : 'sqsft-badge-primary'
                          }
                        `}>
                          {item.badge}
                        </span>
                      )}
                    </div>
                    <p className="text-xs sqsft-caption mt-1 truncate">
                      {item.description}
                    </p>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </nav>

      {/* Bottom Section */}
      <div className="p-6 border-t" style={{ borderColor: 'rgb(var(--color-border))' }}>
        <div className="space-y-1">
          <button className="w-full flex items-center px-4 py-3 text-sm sqsft-nav-item rounded-xl transition-colors duration-200">
            <HelpCircle className="w-4 h-4 mr-3" />
            Help
          </button>
          <button className="w-full flex items-center px-4 py-3 text-sm sqsft-nav-item rounded-xl transition-colors duration-200">
            <Settings className="w-4 h-4 mr-3" />
            Settings
          </button>
        </div>
      </div>
    </motion.aside>
  );
};

export default Sidebar;